import { Component, Input, OnDestroy, OnInit, inject } from '@angular/core';
import { MapGeoUtils } from '@cropx-web/components/map/services/MapGeoUtils';
import { BehaviorSubject, Subscription, combineLatest } from 'rxjs';

import { MapRenderer, baseZIndex } from '../services/map-renderer.service';

@Component({
  selector: 'app-boundary-layer',
  template: '',
  standalone: true,
  providers: [MapRenderer],
})
export class BoundaryLayerComponent implements OnInit, OnDestroy {
  private renderer = inject(MapRenderer);

  map: google.maps.Map;

  @Input('map') set _map(map: google.maps.Map) {
    this.map = map;
    this.renderer.setMap(map);
    this.mapSubject.next(map);
  }

  mapSubject = new BehaviorSubject<google.maps.Map>(null);

  @Input() set style(style: google.maps.Data.StyleOptions) {
    this._style = style;
    this.renderer.setStyleFn((f) => this.getFeatureStyle(f, style));
  }

  private _style: google.maps.Data.StyleOptions = {};

  @Input() set boundary(
    boundaryPolygon: GeoJSON.Polygon | GeoJSON.Feature<GeoJSON.Polygon>,
  ) {
    this.boundarySubject.next(boundaryPolygon);
  }

  boundarySubject = new BehaviorSubject<
    GeoJSON.Polygon | GeoJSON.Feature<GeoJSON.Polygon>
  >(null);

  @Input() set image(image: string) {
    this.imageSubject.next(image);
  }

  @Input() autoCenter = true;

  @Input() set opacity(value: number) {
    this.renderer.setOpacity(value);
  }

  imageSubject = new BehaviorSubject<string>('');

  private subscription: Subscription;

  ngOnInit(): void {
    this.renderer.setStyleFn((f) => this.getFeatureStyle(f, this._style));

    this.subscribeInputs();
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  private subscribeInputs(): void {
    this.subscription = combineLatest([
      this.boundarySubject,
      this.imageSubject,
      this.mapSubject,
    ]).subscribe(([boundary, image, map]) => {
      if (!boundary || !map) {
        return;
      }

      let feature: GeoJSON.Feature = null;
      let geometry: GeoJSON.Polygon = null;

      if (boundary.type === 'Polygon') {
        feature = {
          type: 'Feature',
          geometry: boundary,
          properties: {},
        };
        geometry = boundary;
      } else {
        feature = boundary;
        geometry = boundary.geometry;
      }

      this.renderer.setFeatures([feature]);

      if (image) {
        this.renderer.setBackgroundLayer(
          image,
          MapGeoUtils.GeoJSONBounds(geometry),
        );
      }
      if (this.autoCenter) {
        this.map.fitBounds(MapGeoUtils.GeoJSONBounds(geometry));
      }
    });
  }

  getFeatureStyle(
    feature: google.maps.Data.Feature,
    additionalStyles: google.maps.Data.StyleOptions,
  ): google.maps.Data.StyleOptions {
    return {
      fillOpacity: 0,
      strokeWeight: 2,
      zIndex: baseZIndex - 1,
      clickable: false,
      ...additionalStyles,
    };
  }
}
